import React, { useEffect, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import "../styles/research.scss";
import * as Yup from "yup";
import { useConfigureFooter } from "~hooks/useHelpers";
import ModalForm from "../Components/Modal/ModalForm";
import ValidateFormModal from "~components/Modal/ValidateFormModal";
import useModalAction from "~components/Modal/hooks/useModalAction";
import LoaderFull from "~components/Form/components/LoaderFull";
import Title from "../Components/Form/Create/Title";
import SimpleNumber from "../Components/Form/Create/SimpleNumber";
import Radio from "../Components/Form/Create/Radio";
import Checklist from "../Components/Form/Create/Checklist";
import Select from "../Components/Form/Create/Select";
import File from "../Components/Form/Create/File";
import { useNavigate, useParams } from "react-router-dom";
import { useSalvarPesquisaCliente } from "../hooks/useResearchManagementRequests";
import { initialPath } from "~misc/paths";
import { prepareData } from "../helpers/searchHelpers";
import { db } from "~db/db";
import ModalResponse from "~components/Modal/ModalResponse";
import ClientHeader from "~components/ClientInfo/ClientHeader";
import useCliente from "~fetchs/useCliente";
import { getLocation } from "~services/utils/geoLocation";
import { formatDateToCustomString } from "~pages/PriceSearchV2/utils/searchHelpers";
import { randomId } from "~pages/Funnel/utils";
import { detectFileType } from "~services/utils/validationFile";
import useCheckIn from "~fetchs/Roterizacao/useCheckIn";

const CreateResearch = () => {
  const { parameter: id } = useParams();
  const formikRef = useRef();
  const navigate = useNavigate();

  const [clienteId, setClienteId] = useState(0);
  const [pesquisaCampoCliente, setPesquisaCampoCliente] = useState([]);
  const [pesquisaDados, setPesquisaDados] = useState([]);
  const [formMockup, setFormMockup] = useState([]);
  const [initialValues, setInitialValues] = useState({});
  const [hook, setHook] = useState(0);
  const [toSendSearchs, setToSendSearchs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lockForm, setLockForm] = useState(false);

  const { cliente } = useCliente(clienteId);


   const { checkin } = useCheckIn();




  const [showMessageModal, setShowMessageModal] = useState({
    text: "",
    type: "success",
  });

  const { closeModalAction: closeModalResponse } = useModalAction();

  const onSuccess = async (data) => {
    setLoading(false);

    if (data.data) {
      const { dados } = prepareData([data.data]);
      const toUpdate = dados;

      await db.pesquisaCampoCliente.bulkPut(toUpdate);

      setLoading(false);
      openModalClear(true);
    }
  };

  const onError = (err) => {
    setLoading(false);
    setShowMessageModal((prev) => ({
      ...prev,
      type: "alert",
      text: "Ocorreu um erro durante o envio da pesquisa.",
      submessage: err?.message ?? "",
    }));
  };

  const send = useSalvarPesquisaCliente(toSendSearchs, onSuccess, onError);

  useEffect(() => {
    if (pesquisaDados && Object.entries(pesquisaDados)?.length > 0) {
      if (pesquisaDados.dataResposta) {
        navigate(`${initialPath}/gestao-de-pesquisa/visualizar/${id}`);
      } else {
        const data = new Date(pesquisaDados.data);
        const today = new Date();
        if (today > data) setLockForm(true);

        if (pesquisaCampoCliente?.length > 0) {
          setFormMockup(pesquisaCampoCliente);
        }
      }
    }
  }, [pesquisaCampoCliente, pesquisaDados]);

  useEffect(() => {
    if (formMockup?.length) {
      setInitialValues(
        formMockup.reduce((acc, item) => {
          if (item.type === "checklist") {
            acc[`${item.id}`] = [];
          } else if (item.type === "file") {
            acc[`${item.id}`] = [];
          } else {
            acc[`${item.id}`] = "";
          }
          return acc;
        }, {})
      );
      setHook((prev) => prev + 1);
    }
  }, [formMockup]);

  const fetchData = async () => {
    const filter = await db.pesquisaCampoCliente.where("id").equals(parseInt(id)).first();

    if (id && filter) {
      setClienteId(filter?.clienteId);
      setPesquisaDados(filter);
      setPesquisaCampoCliente(filter.pesquisa || []);
    }
  };

  useEffect(() => {
    if (id > 0) {
      fetchData();
    }
  }, [id]);

  const {
    isModalOpenAction: isModalClear,
    closeModalAction: closeModalclear,
    openModalAction: openModalClear,
  } = useModalAction();

  const allowedExtensions = {
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    png: "image/png",
    gif: "image/gif",
    jpeg: "image/jpeg",
    jpg: "image/jpeg", // Incluído para garantir suporte ao formato
    pdf: "application/pdf",
    csv: "text/csv",
    odt: "application/vnd.oasis.opendocument.text",
    zip: "application/zip",
    msg: "application/vnd.ms-outlook",
    eml: "message/rfc822",
  };

  let validationSchema = Yup.object(
    formMockup.reduce((acc, item) => {
      const key = `${item.id}`;

      switch (item.type) {
        case "text":
        case "date":
          acc[key] = item.required ? Yup.string().required("●ﾠCampo obrigatório") : Yup.string();
          break;

        case "number":
          acc[key] = item.required
            ? Yup.number().typeError("Insira um número válido").required("●ﾠCampo obrigatório")
            : Yup.number().typeError("Insira um número válido");
          break;

        case "radio":
        case "select":
          acc[key] = item.required ? Yup.string().required("●ﾠSelecione uma opção") : Yup.string();
          break;

        case "checklist":
          acc[key] = item.required ? Yup.array().min(1, "●ﾠSelecione pelo menos uma opção") : Yup.array();
          break;

        case "file":
          let fileSchema = Yup.array().test("fileFormat", "●ﾠUm ou mais arquivos têm formato inválido", async (files) => {
            if (!files || files.length === 0) return true;
            const acceptedFormats = item.options
              .map((format) => {
                return allowedExtensions[format.toLowerCase().trim()] ? format.toLowerCase().trim().replace('.','') : false;
              })
              .filter(Boolean);


              console.log(acceptedFormats);
            for (const file of files) {
              const realType = await detectFileType({ file, allowedExtensions });

              console.log(realType);

              const allowed = acceptedFormats.includes(realType);

              if (!allowed) return false;
            }

            return true;
          });

          if (item.required) {
            fileSchema = fileSchema.min(1, "●ﾠPelo menos um arquivo é obrigatório");
          }

          acc[key] = fileSchema;
          break;
      }

      return acc;
    }, {})
  );

const handleSubmit = async (values) => {
  const formData = new FormData();

  let pos = await getLocation();

  Object.keys(pos).forEach((key) => {
    pos[key] = pos[key] ? pos[key].toString() : null;
  });

  const toSend = {
    id,
    ...pos,
    createdAt: formatDateToCustomString(new Date()),
    pesquisa: await Promise.all(
      pesquisaCampoCliente.map(async (e) => {
        let value = Array.isArray(values[e.id]) ? values[e.id] : [values[e.id]];
        const code = randomId(8);

        switch (e.type) {
          case "file":
          case "foto":
            value.forEach((file, i) => {
              if (file) {
                formData.append(`documento_${i}_${code}`, file);
              }
            });
            value = [];
            break;

          default:
            if (Array.isArray(value)) {
              value = value.map((v) => v?.toString?.() ?? "");
            }
            break;
        }

        return {
          ...e,
          code,
          type: e.originalType,
          chosen: value,
        };
      })
    ),
  };

  if (toSend && typeof toSend === "object") {
    formData.append("dados", JSON.stringify({ ...toSend }));
    setToSendSearchs(formData);
  }
};


  useEffect(() => {
    if (toSendSearchs) {
      setLoading(true);
      send();
    }
  }, [toSendSearchs]);

  useConfigureFooter(
    {
      form: pesquisaCampoCliente?.length > 0 && !lockForm,
      labelBack: "Cancelar",
      defaultBack: true,
      hideReturnButton: true,
      formId: "emissor",
      submitLabel: "Salvar",
      submitVariation: "green",
      notDeveloped: false,
    },
    [pesquisaCampoCliente]
  );



  const handleCloseModal = () => {
     const params = new URLSearchParams(globalThis.location.search);
      const origem = params.get("origem");
    if (origem === "hub-geral-de-formularios") {
      navigate(`${initialPath}/gestao-de-pesquisa`);
      return;
    }else if(origem === "carteira-geral") {
      navigate(`${initialPath}/gestao-de-pesquisa/formularios/${cliente.id}?origem=carteira-geral`);
      return;
    }else if(origem === "roteirizacao") {
      navigate(`${initialPath}/gestao-de-pesquisa/formularios/${cliente.id}?origem=roteirizacao`);
      return;
    }

    const url = cliente?.id
      ? `${initialPath}/gestao-de-pesquisa/formularios/${cliente.id}`
      : `${initialPath}/gestao-de-pesquisa`;

    navigate(url);
};




  return (
    <>
      {loading && (
        <div className="flex items-center justify-center w-full p-8">
          <LoaderFull />
        </div>
      )}
      <ClientHeader dados={cliente} />
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange={true}
        validateOnBlur={true}
        key={hook}
      >
        {({ values, setFieldValue, errors, touched, isSubmitting }) => (
          <Form className="container research" id="emissor">
            {lockForm && <>Mensagem</>}
            {formMockup ? (
              <>
                {formMockup[0] && formMockup[0].id === 0 && (
                  <h1 className="research__title">
                    <span>Pesquisa:</span>
                    {formMockup[0] && formMockup[0].value}
                    {/* clienteIndex.length > 0 ? clienteIndex[0]?.title :  */}
                  </h1>
                )}
                {formMockup.map((field) => (
                  <>
                    {field.id !== 0 && (
                      <div
                        key={field.id}
                        className={`research__field bg-white px-7  pt-7 pb-10 rounded-[10px] shadow-lg mt-4 mb-6 ${field.type}`}
                      >
                        <Title field={field} />
                        <div className={`content-${field.type}`}>
                          {["text","date"].includes(field.type) ? (
                            <Field
                              disabled={lockForm}
                              className={errors[`${field.id}`] && touched[`${field.id}`] ? "input-error" : ""}
                              name={`${field.id}`}
                              type={field.type}
                              placeholder={field.placeholder}
                            />
                          ) : field.type === "number" ? (
                            <SimpleNumber
                              isDisabled={lockForm}
                              field={field}
                              errors={errors}
                              touched={touched}
                              values={values}
                              setFieldValue={setFieldValue}
                            />
                          ) : field.type === "radio" ? (
                            <Radio
                              isDisabled={lockForm}
                              field={field}
                              errors={errors}
                              touched={touched}
                              values={values}
                              setFieldValue={setFieldValue}
                            />
                          ) : field.type === "checklist" ? (
                            <Checklist
                              isDisabled={lockForm}
                              field={field}
                              errors={errors}
                              touched={touched}
                              values={values}
                              setFieldValue={setFieldValue}
                            />
                          ) : field.type === "select" ? (
                            <>
                              <Select field={field} errors={errors} touched={touched} isDisabled={lockForm} />
                            </>
                          ) : ["file", "foto"].includes(field.type) ? (
                            <>
                              <File
                                isDisabled={lockForm}
                                field={field}
                                errors={errors}
                                touched={touched}
                                values={values}
                                setFieldValue={setFieldValue}
                                formikRef={formikRef}
                              />
                            </>
                          ) : null}
                        </div>
                        <ErrorMessage
                          name={`${field.id}`}
                          component="div"
                          className="   text-[#6F0D12] mt-2 bg-[#F7D1D3] p-4 ml-[1.3rem] mt-[1rem] rounded-[5px]"
                        />
                      </div>
                    )}
                  </>
                ))}
              </>
            ) : (
              <>
                <LoaderFull />
              </>
            )}

            <ValidateFormModal errors={errors} isSubmiting={isSubmitting} notification={true} />

            {showMessageModal.text ? (
              <ModalResponse
                isOpen={true}
                closeModal={() => {
                  const onClose = showMessageModal.onClose;
                  setShowMessageModal((prev) => ({
                    ...prev,
                    text: "",
                    onClose: () => {},
                  }));

                  closeModalResponse();

                  if (onClose && typeof onClose == "function") onClose();
                }}
                status={showMessageModal.type ?? ""}
                message={showMessageModal.text ?? ""}
                submessage={showMessageModal.submessage ?? ""}
                button="Ok"
              />
            ) : null}

           <ModalForm
              isOpen={isModalClear}
              onClose={handleCloseModal}
              title={
                <>
                  <p className="">Formulário preenchido com sucesso!</p>
                </>
              }
              status="submit"
            />


          </Form>
        )}
      </Formik>
    </>
  );
};

export default CreateResearch;
